import React, { RefObject, useCallback, useRef, useState } from "react";
import { useFormContext, PathValue, Path, FieldValues } from "react-hook-form";

import { useClickOutside } from "@/lib/hooks/use-click-outside";
import { cn } from "@/lib/utils";
import {
  CommandField,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "./CommandField";
interface SearchCommandProps<T extends FieldValues> {
  setIsValid: (isValid: { id: string; text: string }) => void;
  valueList: { id: string; text: string }[];
  formFields: {
    formName: Path<T>;
    placeholder: string;
    label: string;
  };
  filterAction: (value: string) => void;
  emptyMessage: React.ReactNode;
  additionalMessage?: React.ReactNode;
  noResults?: boolean;
  isFetchingData?: boolean;
  showLabel?: boolean;
  commandListClasses?: string;
  commandInputClasses?: string;
  commandItemClasses?: string;
  onSelectCallback?: (value: { id: string; text: string }) => void;
  /**
   * Closes the list when the input loses focus. Turned off, the list stays open until a
   * suggestion is picked or a press lands outside the input and the list — so it outlives
   * a dismissed on-screen keyboard.
   */
  shouldCloseOnBlur?: boolean;
}

const SearchCommand = <T extends FieldValues>({
  setIsValid,
  valueList,
  formFields,
  filterAction,
  emptyMessage,
  additionalMessage,
  showLabel = true,
  commandListClasses,
  commandInputClasses,
  commandItemClasses,
  onSelectCallback,
  shouldCloseOnBlur = true,
}: SearchCommandProps<T>) => {
  const { formName, placeholder, label } = formFields;
  const [inputValue, setInputValue] = useState("");
  const [isCommandOpen, setCommandOpen] = useState(false);
  const [doneLoadingValues, setDoneLoadingValues] = useState(false);
  const form = useFormContext<T>();
  const fieldRef = useRef<HTMLDivElement>(null);

  // The field wraps both the input and the list, so a press on either is not outside.
  // Left to blur otherwise, which already closes the list on any press elsewhere.
  const handleClickOutside = useCallback(() => {
    if (!shouldCloseOnBlur) {
      setCommandOpen(false);
    }
  }, [shouldCloseOnBlur]);

  useClickOutside(fieldRef as RefObject<HTMLDivElement>, handleClickOutside);

  const handleOpenChange = (open: boolean) => {
    if (!open && !shouldCloseOnBlur) {
      return;
    }

    if (!open) {
      // Added a small delay when closing to allow for item selection
      setTimeout(() => {
        setCommandOpen(false);
      }, 200);
    } else {
      setCommandOpen(true);
    }
  };

  return (
    <CommandField
      ref={fieldRef}
      name={formName}
      label={label}
      placeholder={placeholder}
      onOpenChange={handleOpenChange}
      className="tw-space-y-0 tw-mt-0"
      labelClassName={cn("md:tw-text-gray-600 tw-text-gray-900", {
        "tw-hidden": !showLabel,
      })}
    >
      <CommandInput
        onValueChange={(value) => {
          setDoneLoadingValues(false);
          setCommandOpen(true);
          setInputValue(value);
          filterAction(value);
          form.setValue(formName, value as PathValue<T, Path<T>>);
          setDoneLoadingValues(true);
        }}
        placeholder={placeholder}
        errorMsg={form.formState.errors[formName]?.message as string}
        className={cn(
          "tw-border-0 focus:tw-outline-none focus:tw-border-none focus-within:tw-border-0 tw-pl-0 tw-py-0",
          commandInputClasses,
        )}
      />
      <CommandList
        open={isCommandOpen}
        className={cn(
          "tw-w-screen tw-fixed tw-left-0 tw-top-[170px] !tw-rounded-0 !tw-shadow-none tw-border-t-[1px] tw-border-gray-300",
          commandListClasses,
        )}
      >
        {doneLoadingValues && inputValue.length > 0 && !valueList.length && (
          <CommandEmpty>{emptyMessage}</CommandEmpty>
        )}
        <CommandGroup>
          {!!valueList.length &&
            valueList.map((value) => (
              <CommandItem
                className={cn(
                  "tw-border-b-0 tw-px-80 tw-py-8",
                  commandItemClasses,
                )}
                key={value.id}
                value={value.text || ""}
                searchValue={inputValue}
                onSelect={(value) => {
                  const fullValue = valueList.find(
                    (item) => item.text === value,
                  );
                  setIsValid({
                    id: fullValue?.id || "",
                    text: fullValue?.text || "",
                  });
                  form.setValue(formName, value as PathValue<T, Path<T>>);
                  // this timeout is needed for the form validation to not run early
                  setTimeout(() => {
                    form.trigger(formName);
                    // Without blur to close it, picking a value is what does.
                    if (shouldCloseOnBlur) {
                      handleOpenChange(true);
                    } else {
                      setCommandOpen(false);
                    }
                    onSelectCallback?.(fullValue || { id: "", text: "" });
                  }, 100);
                }}
              >
                {value.text}
              </CommandItem>
            ))}
        </CommandGroup>
        {valueList.length > 0 && additionalMessage}
      </CommandList>
    </CommandField>
  );
};

export default SearchCommand;
